<template>
  <div class="tile">
    <LoadingSpinner class="tile__spinner" v-if="!learningPath.difficulty" />
    <div class="tile__instructor">
      <transition-group name="fade">
        <div
          class="tile__instructor-container"
          v-for="(instructor, index) in learningPath.instructors"
          :key="instructor.id"
          v-show="index === currentInstructorIndex"
          @mouseover="moreThanOneInstructor && pause()"
          @mouseleave="moreThanOneInstructor && resume()"
        >
          <div class="tile__instructor-avatar">
            <ChevronLeftIcon
              v-if="moreThanOneInstructor"
              @click="previousInstructor()"
            />
            <div
              class="tile__instructor-avatar-img"
              :style="`background-image: url(${instructor.profile_img_url})`"
            >
              <div
                v-if="moreThanOneInstructor"
                class="tile__instructor-avatar-img-counter"
              >
                +{{ learningPath.instructors.length - 1 }}
              </div>
            </div>
            <ChevronRightIcon
              v-if="moreThanOneInstructor"
              @click="nextInstructor()"
            />
          </div>
          <div class="tile__instructor-info">
            <span class="tile__instructor-info-name">
              {{ instructor.name }}
            </span>
            <span v-if="index === 0" class="tile__instructor-info-role">
              Primary Instructor
            </span>
          </div>
        </div>
      </transition-group>
    </div>
    <SummaryTile
      :learningAreaColor="learningAreaColor"
      :certificate="certificate"
      :summary="summary"
    />
  </div>
</template>
<script>
import { mapState } from 'vuex'
import { ChevronLeftIcon, ChevronRightIcon } from 'vue-feather-icons'
import LoadingSpinner from '@/components/loaders/LoadingSpinner.vue'
import SummaryTile from './SummaryTile.vue'

export default {
  name: 'InstructorTile',
  components: {
    LoadingSpinner,
    SummaryTile,
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  props: {
    learningPath: {
      type: Object,
      required: true,
    },
    learningAreaColor: {
      type: String,
      required: true,
    },
  },
  data: () => {
    return { currentInstructorIndex: 0 }
  },
  mounted() {
    if (this.moreThanOneInstructor)
      this.startCarrousel(this.nextInstructor, 5000)
  },
  computed: {
    ...mapState({
      certificationsCache: state => state.certifications.certificationsCache,
    }),
    certificate() {
      return this.certificationsCache[this.learningPath.certifications[0]] || {}
    },
    moreThanOneInstructor() {
      return this.learningPath?.instructors?.length > 1
    },
    duration() {
      const { duration_in_seconds: duration } = this.learningPath
      return this.$duration.durationFromSecs(duration)
    },
    summary() {
      const { learningPath, certificate, duration } = this
      const { summary, difficulty } = learningPath

      return {
        DIFFICULTY: difficulty,
        DURATION: duration,
        SECTIONS: summary.total_groups,
        COURSES: summary.total_courses,
        VIDEOS: summary.total_videos,
        QUIZZES: summary.total_quizzes,
        LABS: summary.total_labs,
        CERTIFICATION: certificate.short_name,
      }
    },
  },
  methods: {
    nextInstructor() {
      const instructors = this.learningPath?.instructors

      if (instructors.length !== this.currentInstructorIndex + 1)
        this.currentInstructorIndex += 1
      else this.currentInstructorIndex = 0
    },
    previousInstructor() {
      const instructors = this.learningPath?.instructors

      if (this.currentInstructorIndex !== 0) this.currentInstructorIndex -= 1
      else this.currentInstructorIndex = instructors.length - 1
    },
    startCarrousel(callback, delay) {
      let timerId
      let start
      let remaining = delay

      this.pause = () => {
        window.clearTimeout(timerId)
        remaining -= new Date() - start
      }

      const resume = () => {
        start = new Date()
        timerId = window.setTimeout(() => {
          remaining = delay
          resume()
          callback()
        }, remaining)
      }

      this.resume = resume

      this.resume()
    },
  },
  beforeDestroy() {
    if (this.pause) this.pause()
  },
}
</script>
<style lang="scss" scoped>
.tile {
  display: flex;
  width: 100%;
  height: fit-content;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  gap: 30px;
  background: var(--glass-dark);
  backdrop-filter: blur(22px);

  &__spinner {
    margin: unset;
    background-color: unset;
    z-index: unset;
    display: flex;
    justify-content: center;
    flex-flow: column;
  }
  &__instructor {
    position: relative;
    height: 150px;
    width: 100%;

    .fade-enter-active,
    .fade-leave-active {
      transition: opacity 1s;
    }

    &-container {
      color: var(--white);
      position: absolute;
      left: 0;
      width: 100%;
    }
    &-avatar {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;

      svg {
        cursor: pointer;
      }

      &-img {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        position: relative;
        background-color: var(--white);
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;

        &-counter {
          display: flex;
          justify-content: center;
          align-items: center;
          position: absolute;
          top: 0;
          right: 0;
          width: 29px;
          height: 29px;
          border-radius: 50%;
          font-size: rem(12px);
          line-height: rem(14px);
          font-family: var(--font-book);
          background: var(--dark-brighter);
        }
      }
    }
    &-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 10px;

      &-name {
        font-size: rem(20px);
        line-height: rem(26px);
        font-family: var(--font-bold);
      }
      &-role {
        font-size: rem(12px);
        line-height: rem(14px);
        font-family: var(--font-book);
        color: var(--light-blue-gray);
      }
    }
  }
}
</style>
